import { Component, ViewChild, OnInit } from '@angular/core';
import { SocietesLexiClient, SocieteToCreateDto } from '@lexi-clients/lexi';
import { DxFormComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { ValidationResult } from 'devextreme/ui/validation_group';
import { lastValueFrom } from 'rxjs';
import { NotificationType } from '../../modules/shared/references/references';
import { Router } from '@angular/router';
import { AuthService } from '../../settings/auth.service';


@Component({
    selector: 'app-creation-societe',
    templateUrl: './creation-societe.component.html',
    styleUrls: ['./creation-societe.component.scss'],
    standalone: false
})
export class CreationSocieteComponent implements OnInit {
  @ViewChild(DxFormComponent) form: DxFormComponent;
  
  // Wizard state
  currentStep = 1;
  maxSteps = 4;
  
  // Form data - Utilisation du DTO complet avec tous les nouveaux champs
  societeToCreate: SocieteToCreateDto = {
    codeBo: '',
    intitule: '',
    raisonSociale: '',
    adresse1: '',
    adresse2: '',
    codePostal: '',
    ville: '',
    pays: '',
    email: '',
    telephone1: '',
    telephone2: '',
    fax: '',
    identifiantFiscal: '',
    siteCodeBo: '',
    siteIntitule: '',
    enabled: true,
    gestionStock: false,
    fretLocalActif: false,
    interfaceComptableActive: false
  };
  codeDossier: string;
  requeteServeurEnCours = false;

  constructor(
    private readonly societeService : SocietesLexiClient,
    private readonly router: Router,
    private readonly auth: AuthService
  ) {}

  ngOnInit() {
    // Initialisation déjà faite dans la déclaration de societeFormData
  }

  get currentStepProgress(): number {
    return (this.currentStep / this.maxSteps) * 100;
  }

  // Navigation methods
  nextStep() {
    if (this.canProceedToNextStep() && this.currentStep < this.maxSteps) {
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  // Validation methods
  canProceedToNextStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.validateStep1();
      case 2:
        return this.validateStep2();
      case 3:
        return this.validateStep3();
      default:
        return true;
    }
  }

  private validateStep1(): boolean {
    return !!(this.societeToCreate.codeBo && 
              this.societeToCreate.intitule &&
              this.societeToCreate.codeBo.length > 0 &&
              this.societeToCreate.intitule.length >= 2 &&
              this.isValidCode(this.societeToCreate.codeBo));
  }

  private validateStep2(): boolean {
    return !!(this.societeToCreate.siteCodeBo && 
              this.societeToCreate.siteIntitule &&
              this.societeToCreate.siteCodeBo.length > 0 &&
              this.societeToCreate.siteIntitule.length >= 2 &&
              this.isValidCode(this.societeToCreate.siteCodeBo));
  }

  private validateStep3(): boolean {
    // Step 3 has no required fields, only optional configuration
    return true;
  }

  canCreateSociete(): boolean {
    return this.validateStep1() && this.validateStep2() && this.validateStep3();
  }

  private isValidCode(code: string): boolean {
    const pattern = /^[A-Z0-9_-]+$/;
    return pattern.test(code);
  }

  // Email validation
  private isValidEmail(email: string): boolean {
    if (!email) return true; // Email is optional
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  }

  // Validation de l'identifiant fiscal
  private isValidIdentifiantFiscal(identifiantFiscal: string): boolean {
    if (!identifiantFiscal) return true; // Identifiant fiscal is optional
    if (identifiantFiscal.length > 20) return false;
    const identifiantFiscalPattern = /^[A-Z0-9\-\.]+$/;
    return identifiantFiscalPattern.test(identifiantFiscal);
  }

  // Validation du code postal
  private isValidCodePostal(codePostal: string): boolean {
    if (!codePostal) return true; // Code postal is optional
    const codePostalPattern = /^\d{5}$/;
    return codePostalPattern.test(codePostal);
  }



  async creer() {
    try {
      // Final validation
      if (!this.canCreateSociete()) {
        notify({ 
          closeOnClick: true, 
          message: "Veuillez vérifier les informations saisies" 
        }, NotificationType.Error);
        return;
      }

          // Email validation if provided
      if (this.societeToCreate.email && !this.isValidEmail(this.societeToCreate.email)) {
        notify({ 
          closeOnClick: true, 
          message: "Le format de l'email est invalide" 
        }, NotificationType.Error);
        return;
      }

      // Identifiant fiscal validation if provided  
      if (this.societeToCreate.identifiantFiscal && !this.isValidIdentifiantFiscal(this.societeToCreate.identifiantFiscal)) {
        notify({ 
          closeOnClick: true, 
          message: "Le format de l'identifiant fiscal est invalide (maximum 20 caractères, lettres, chiffres, tirets et points uniquement)" 
        }, NotificationType.Error);
        return;
      }

      // Code postal validation if provided
      if (this.societeToCreate.codePostal && !this.isValidCodePostal(this.societeToCreate.codePostal)) {
        notify({ 
          closeOnClick: true, 
          message: "Le format du code postal est invalide (5 chiffres)" 
        }, NotificationType.Error);
        return;
      }

      // Création de la nouvelle société
      this.requeteServeurEnCours = true;
      const s = await lastValueFrom(this.societeService.create(this.societeToCreate));
      
      notify({ 
        closeOnClick: true, 
        message: "La société a été créée avec succès. Changement de société..." 
      }, NotificationType.Success);

      // Redirection vers la nouvelle société créée
      await this.auth.refreshAvailableSocietesFromServer(this.auth.securityUser?.id);
      await this.auth.setSocieteId(s.id, true);
      
      this.router.navigate(['']);
    }
    catch (error) {
      console.error('Erreur lors de la création de la société:', error);
      notify({ 
        closeOnClick: true, 
        message: "Une erreur est survenue lors de la création de la société" 
      }, NotificationType.Error);
    }
    finally {
      this.requeteServeurEnCours = false;
    }
  }

  // Helper method for step validation (used in template)
  isStepValid(step: number): boolean {
    switch (step) {
      case 1: return this.validateStep1();
      case 2: return this.validateStep2();
      case 3: return this.validateStep3();
      default: return false;
    }
  }
}
